// CSS import order below is load-bearing and must stay at the top of this file.
// Mantine declares its stock palette on `:root, :host`; the generated brand
// token layer redeclares `--mantine-color-*` on `:root`. Identical specificity,
// so whichever sheet the bundler emits *last* wins. CSS files are ES modules and
// are evaluated once, at their first import — so if anything above them pulls in
// the token layer transitively (GlobalProviders -> @cowbell/ui ->
// MantineThemeProvider imports all four), the tokens get pinned *ahead* of
// Mantine and the whole app silently falls back to stock Mantine colors.
// Keep these first, and keep Mantine ahead of the brand layer.
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';

// Brand design tokens (generated output — do not edit the .gen.css files by hand).
// NOTE: Using relative path import instead of @cowbell/ui package path because:
// - In Nx workspaces, CSS files from libraries are not automatically included in the build output
// - The ui library build config (rollup.config.cjs) doesn't include CSS files as assets
// - Package imports (@cowbell/ui/*) resolve to dist/libs/ui, but CSS files aren't copied there
// - Relative imports work in development and are resolved by the bundler (Vite/webpack) at build time
// - This matches the pattern used in libs/ui/src/lib/theme/ThemeProvider.tsx
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/theme/vars/primitive.vars.gen.css';
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/theme/vars/core.vars.gen.css';
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/theme/vars/dark.vars.gen.css';
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/theme/vars/light.vars.gen.css';

// Re-tints the generated Mantine dark ramp onto the MUI theme's blue-tinged
// near-black so Mantine surfaces stop reading warm next to the navigation.
// Hand-authored, and must stay below the generated layer it overrides.
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/theme/dark-tint.overrides.css';

// The motion standard's curves and durations, which every expand, collapse and slide reads. Imported here for the same
// reason as the token files: a library's style sheet is not part of its package build.
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../libs/ui/src/lib/motion/motion.css';

// Every scrollbar's look. Bundled rather than in public/index.css, which the edge caches under a fixed URL.
import './scrollbars.css';

import { StrictMode } from 'react';
import './config/init.config';

import { createRoot } from 'react-dom/client';

import { GlobalErrorBoundary } from './components/error-boundaries/GlobalErrorBoundary';
import GlobalProviders from './providers/GlobalProviders';

import { BrowserMappings } from './components/main/common/BrowserMappings';
import { BrowserUnsupported } from './components/main/common/BrowserUnsupported';

import './_lib/validations/string.validations';
import './config/numeral/Numeral';

import 'typeface-oswald';
import 'typeface-titillium-web';
import App from './App';
import './i18n/i18n';
import { installHighchartsContainerReflow } from './config/highcharts/containerReflow';

// Highcharts only auto-resizes on window resize; this teaches every chart to
// follow its own container instead. Must run before the first chart mounts.
installHighchartsContainerReflow();

const $root = document.getElementById('root') as HTMLElement;

const browser = BrowserMappings();
if (browser === 'Microsoft Internet Explorer') {
  createRoot($root).render(
    <StrictMode>
      <GlobalProviders>
        <BrowserUnsupported />
      </GlobalProviders>
    </StrictMode>
  );
} else {
  createRoot($root).render(
    <GlobalProviders>
      <GlobalErrorBoundary>
        <App />
      </GlobalErrorBoundary>
    </GlobalProviders>
  );
}

// Add global error handler to filter out extension-related errors
window.addEventListener('error', (event) => {
  // Check if the error is from an extension
  if (
    event.filename?.includes('sidePanelUtil.js') ||
    event.filename?.includes('GenAIWebpageEligibilityService.js') ||
    event.filename?.includes('content-script-utils.js')
  ) {
    event.preventDefault();
    return true;
  }
});

// After a new deploy, a tab left open still holds React.lazy() closures
// pointing at the previous build's chunk hashes. Navigating to a
// not-yet-loaded route then fails to fetch that stale chunk (404, or an
// HTML fallback page mistaken for JS), which Vite surfaces as this event.
// Left unhandled, the failed import corrupts the in-flight React commit,
// surfacing downstream as confusing errors like "X is not a function" or
// "'text/html' is not a valid JavaScript MIME type". Reload once to pick
// up the current build instead of leaving the user on a broken page.
const STALE_CHUNK_RELOAD_THROTTLE_MS = 10_000;
const staleChunkReloadedAtKey = 'cowbell:stale-chunk-reloaded-at';

globalThis.addEventListener('vite:preloadError', (event) => {
  const reloadedAt = Number(sessionStorage.getItem(staleChunkReloadedAtKey));
  const reloadedRecently =
    Date.now() - reloadedAt < STALE_CHUNK_RELOAD_THROTTLE_MS;

  if (reloadedRecently) {
    // Don't call event.preventDefault() here: Vite's preload helper only
    // swallows the failed import when the default is prevented, in which
    // case it resolves the lazy import with `undefined` instead of
    // rejecting. That turns a recoverable chunk-load failure into
    // `Cannot read properties of undefined (reading 'default')` inside
    // React.lazy. Letting it stay unprevented means Vite rethrows the real
    // error so the ErrorBoundary can show it instead.
    return;
  }

  event.preventDefault();
  sessionStorage.setItem(staleChunkReloadedAtKey, String(Date.now()));
  globalThis.location.reload();
});
